import kebabCase from 'lodash/kebabCase';
import React, { MouseEventHandler } from 'react';
import { useTheme } from 'styled-components';
import { generateKeyForElement } from '../../../../helpers/elementKeyHelper';
import Collapsible from '../../../Collapsible';
import Icon from '../../../Icon';
import { NestedPrimaryNavItem } from '../../types';
import SimplePrimaryNavSubmenu from './SimplePrimaryNavSubmenu';
import { NestedPrimaryNavSubmenuHeading, StyledGridAlignedText } from './styles';
import { NavSubmenuProps } from './types';

type NestedPrimaryNavSubmenuProps = NavSubmenuProps<NestedPrimaryNavItem> & {
  activeNestedItemIndex: number | null;
  setActiveNestedItemIndex: (newIndex: number | null, closeIfOpen: boolean) => void;
  resetNav: () => void;
};

const getSubsectionId = (baseId: string, name: string, index: number): string =>
  `${baseId}-${kebabCase(name)}-subsection-${index}`;

const Heading: React.FC<NavSubmenuProps<NestedPrimaryNavItem>> = ({
  baseId,
  navItem: { name, primarySection, onCollapse, onExpand },
  isActive,
  toggleActive,
}) => {
  const theme = useTheme();

  const controlledIds = primarySection.subsections.map((_, i) =>
    getSubsectionId(baseId, name, i)
  );

  const onClickWithCallback: MouseEventHandler = (event) => {
    if (onExpand && !isActive) {
      onExpand(event);
    }
    if (onCollapse && isActive) {
      onCollapse(event);
    }
    toggleActive();
  };

  return (
    <NestedPrimaryNavSubmenuHeading
      id={baseId}
      isActive={isActive}
      onClick={onClickWithCallback}
      aria-expanded={isActive}
      aria-controls={controlledIds.join(' ')}>
      <StyledGridAlignedText>
        {name}
        <Icon
          name="arrow-down"
          size="small"
          flipY={isActive}
          color={theme.colors.core01}
        />
      </StyledGridAlignedText>
    </NestedPrimaryNavSubmenuHeading>
  );
};

const NestedNavSubmenu: React.FC<NestedPrimaryNavSubmenuProps> = ({
  baseId,
  navItem,
  isActive,
  toggleActive,
  activeNestedItemIndex,
  setActiveNestedItemIndex,
  resetNav,
}) => {
  const onSectionHeadingClick = (index: number) => () => {
    if (activeNestedItemIndex !== null && activeNestedItemIndex < index) {
      // See a similar comment on the PrimaryNavMobile/index.tsx. This will only work
      // satisfactorily if the number of section headings is few enough, which should be
      // the case
      document
        .getElementById(getSubsectionId(baseId, navItem.name, activeNestedItemIndex))
        ?.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveNestedItemIndex(index, true);
  };

  return (
    <>
      <Heading
        baseId={baseId}
        navItem={navItem}
        isActive={isActive}
        toggleActive={toggleActive}
      />
      <Collapsible isOpened={isActive}>
        {navItem.primarySection.subsections.map((section, index) => (
          <SimplePrimaryNavSubmenu
            baseId={getSubsectionId(baseId, navItem.name, index)}
            key={generateKeyForElement(section)}
            navItem={section}
            isActive={activeNestedItemIndex === index}
            toggleActive={onSectionHeadingClick(index)}
            resetNav={resetNav}
          />
        ))}
      </Collapsible>
    </>
  );
};

export default NestedNavSubmenu;
